import type { FC } from "react";

import { isOneOf } from "@arti/utilities";

import { useTabParam } from "../../hooks/useTabParam";
import { useTeamsChatSdkEnabled } from "../../hooks/useTeamsChatSdkEnabled";
import { Tabs } from "../common/Tabs";
import { IssueIntegrationsPanel } from "../issues/IssueIntegrationsPanel";
import { IndexingPanel } from "../sourceControl/IndexingPanel";
import SourceControlPanel from "../sourceControl/SourceControlPanel";
import { TeamsIntegrationPanel } from "./TeamsIntegrationPanel";

type IntegrationsTab = "issues" | "source-control" | "indexing" | "teams";

const INTEGRATIONS_TAB_IDS = [
  "issues",
  "source-control",
  "indexing",
  "teams",
] as const satisfies readonly IntegrationsTab[];

const TABS = [
  { id: "issues" as const, label: "Issue Tracking" },
  { id: "source-control" as const, label: "Source Control" },
  { id: "indexing" as const, label: "Indexing" },
  { id: "teams" as const, label: "Teams" },
];

export const IntegrationsPanel: FC = () => {
  const [activeTab, setTab] = useTabParam<IntegrationsTab>("issues");
  const teamsChatSdk = useTeamsChatSdkEnabled();
  const tabs = teamsChatSdk.enabled
    ? TABS
    : TABS.filter((tab) => tab.id !== "teams");
  const selectedTab =
    activeTab === "teams" && !teamsChatSdk.enabled ? "issues" : activeTab;

  return (
    <div className="space-y-4">
      <Tabs
        tabs={tabs}
        activeTab={selectedTab}
        onChange={(id) => {
          if (isOneOf(id, INTEGRATIONS_TAB_IDS)) {
            setTab(id);
          }
        }}
      />

      {selectedTab === "issues" && <IssueIntegrationsPanel />}
      {selectedTab === "source-control" && <SourceControlPanel />}
      {selectedTab === "indexing" && <IndexingPanel />}
      {selectedTab === "teams" && <TeamsIntegrationPanel />}
    </div>
  );
};
